<template>
    <section
        ref="sectionEl"
        :class="[
            sectionClass,
            {
                'max-h-none ': showMore,
                'max-h-48 overflow-hidden': !showMore,
            },
        ]"
    >
        <slot name="content"></slot>
        <transition name="fade">
            <div
                v-if="!showMore && isOverflowing"
                class="floating-mask"
                :style="{
                    '--expandable-section-mask-end-color': maskGradientEndColor,
                }"
            ></div>
        </transition>
    </section>
    <button
        v-if="isOverflowing"
        type="button"
        :class="buttonClass"
        @click="toggleShowMore"
    >
        {{ btnText }}
    </button>
</template>

<script>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';

export default {
    name: 'expandableSection',
    components: {},
    props: {
        sectionClass: {
            type: String,
            default:
                'self-stretch py-4 flex-col justify-start items-start gap-4 flex transition-all ease-in-out duration-300 relative',
        },
        buttonClass: {
            type: String,
            default:
                "flex justify-center items-center text-slate-800 dark:text-slate-100 text-sm font-medium font-['Inter'] leading-tight mt-4 focus:outline-none hover:text-slate-500 active:text-slate-500 rounded",
        },
        maskGradientEndColor: {
            type: String,
            default: 'rgba(255, 255, 255, 1)',
        },
    },
    setup() {
        const isOverflowing = ref(false);
        const showMore = ref(false);
        const sectionEl = ref(null);
        const maxHeight = ref(0);

        const btnText = computed(() => {
            return showMore.value ? 'Show less' : 'Show more';
        });

        const checkOverflow = () => {
            if (sectionEl.value) {
                const scrollHeight = sectionEl.value.scrollHeight;
                const clientHeight = sectionEl.value.clientHeight;
                const difference = scrollHeight - clientHeight;
                isOverflowing.value = difference >= 9;
            }
        };

        const calculateHeight = () => {
            if (sectionEl.value) {
                maxHeight.value = sectionEl.value.scrollHeight;
            }
        };

        const toggleShowMore = () => {
            showMore.value = !showMore.value;
            calculateHeight();
        };

        watch(sectionEl, (newVal) => {
            if (newVal) {
                checkOverflow();
                calculateHeight();
            }
        });

        onMounted(() => {
            checkOverflow();
            window.addEventListener('resize', checkOverflow);
        });

        onUnmounted(() => {
            window.removeEventListener('resize', checkOverflow);
        });
        return {
            btnText,
            isOverflowing,
            maxHeight,
            sectionEl,
            showMore,
            toggleShowMore,
        };
    },
};
</script>

<style lang="scss" scoped>
.floating-mask {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(
        to bottom,
        rgba(255, 255, 255, 0),
        var(--expandable-section-mask-end-color, rgba(255, 255, 255, 1))
    );
    pointer-events: none;
    transition: opacity 0.3s ease;
}
:where(.dark) .floating-mask {
    background: linear-gradient(
        to bottom,
        rgba(30, 41, 59, 0),
        var(--expandable-section-mask-end-color-dark, rgba(30, 41, 59, 1))
    );
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
